import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { communityCopy, communityMedia } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const CommunitySection = () => {
  return (
    <section
      data-testid="community-section"
      className="bg-[var(--strollee-surface)] px-6 lg:px-10 py-20 lg:py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="community-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {communityCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {communityCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {communityCopy.support}
        </p>
      </motion.div>

      <div className="mt-10 lg:mt-12 grid grid-cols-12 gap-5 lg:gap-6">
        {communityMedia.map((tile, i) => (
          <motion.div
            key={tile.id}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
            data-testid={`community-tile-${tile.id}`}
            className={`group relative overflow-hidden rounded-[var(--strollee-radius-module)] ${tile.span} ${tile.height}`}
          >
            <img
              src={tile.url}
              alt={tile.alt}
              style={tile.position ? { objectPosition: tile.position } : undefined}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#171713]/35 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
            <span className="absolute bottom-4 left-4 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md">
              {tile.label}
            </span>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
        className="mt-12 lg:mt-14 flex items-center justify-between gap-10 border-t border-[var(--strollee-line)] pt-8"
      >
        <div data-testid="community-quote" className="flex items-center gap-6">
          <div className="flex items-center gap-1" aria-label="Five star rating placeholder">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={15} className="fill-[var(--strollee-accent)] text-[var(--strollee-accent)]" />
            ))}
          </div>
          <div>
            <p className="text-base lg:text-lg font-semibold tracking-[-0.01em] text-[var(--strollee-ink)]">
              {communityCopy.quote}
            </p>
            <p className="mt-1 text-[13px] text-[var(--strollee-muted)]">
              {communityCopy.quoteAttribution}
            </p>
          </div>
        </div>
        <a
          href="#reviews"
          data-testid="community-cta"
          className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-colors duration-200 hover:text-[var(--strollee-accent)]"
        >
          {communityCopy.cta}
          <ArrowRight
            size={16}
            strokeWidth={2.2}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </a>
      </motion.div>
    </section>
  );
};
